import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { CheckboxTable } from './CheckboxTable';
import { ConfirmationModal } from './ConfirmationModal';
import { Dropdown } from './Dropdown';
import { LineInfo } from './LineInfo';
import { Button } from './buttons/Button';
import { axios } from '../api';
import { Close } from '../assets/Close';
import { digitsOptions } from '../dropdownOptions';
import { useShippingRates } from '../hooks/useShippingRates';
import { Option } from '../pages/types';
import { ReplacementOrderHeader } from '../tableHeaders';
import {
  BOTTOM_TOAST,
  defaultShippingOption,
  formatAddress,
  generateErrorMsg,
  generateFullName,
  getCountryFlag,
} from '../utils';

export const ReplacementOrderModal = ({ customer, order, onConfirm, onCancel }: any) => {
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [selectedShippingOption, setSelectedShippingOption] = useState<Option>({
    value: '',
    label: 'Select shipping method',
  });
  const [shippingAddress, setShippingAddress] = useState<string>();
  const [tableData, setTableData] = useState<any[]>([]);
  const [quantities, setQuantities] = useState<Record<number, number>>({});
  const { addresses } = customer;
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isLoadingReplaceOrder, setIsLoadingReplaceOrder] = useState(false);
  const { shippingOptions, isLoadingShippingRates } = useShippingRates(order.id);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleCheckboxChange = (index: number) => {
    if (selectedRows.includes(index)) {
      setSelectedRows((prevSelected) => prevSelected.filter((rowIndex) => rowIndex !== index));
    } else {
      setSelectedRows((prevSelected) => [...prevSelected, index]);
    }
  };

  const handleQuantityChange = useCallback((index: number, quantity: number) => {
    setQuantities((prev) => ({
      ...prev,
      [index]: quantity,
    }));
  }, []);

  useEffect(() => {
    const primaryShippingAddress = addresses.find(
      (address: any) => address.type === 'SHIPPING' && address.is_primary
    );
    if (primaryShippingAddress) {
      setShippingAddress(formatAddress(primaryShippingAddress));
    }
  }, [addresses]);

  useEffect(() => {
    const expeditedOption = defaultShippingOption(shippingOptions, customer);
    if (expeditedOption) {
      setSelectedShippingOption(expeditedOption);
    } else {
      setSelectedShippingOption(shippingOptions[0]);
    }
  }, [shippingOptions, customer.default_shipping_method_id, customer]);

  // Initialize quantities only once on mount
  useEffect(() => {
    const initialQuantities = order.products.reduce(
      (acc: Record<number, number>, product: any, index: number) => {
        acc[index] = product.quantity;
        return acc;
      },
      {}
    );
    setQuantities(initialQuantities);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update tableData whenever relevant dependencies change
  useEffect(() => {
    setTableData(
      order.products.map((product: any, index: number) => {
        return {
          'Pet Name': product.pet_plan?.pet_name,
          Quantity: (
            <div className="w-[70px] mr-[10px]">
              <Dropdown
                options={digitsOptions.slice(0, product.quantity)}
                isDisabled={!selectedRows.includes(index)}
                selectedOption={digitsOptions.find(
                  (option) => option.value === String(quantities[index] || product.quantity)
                )}
                setSelectedOption={(newSelection) =>
                  handleQuantityChange(index, +newSelection.value)
                }
              />
            </div>
          ),
          'Product Code': product.code,
          'Product Description': (
            <div className="w-[300px]">{product.description_bark ?? '--'}</div>
          ),
          index,
          ...product,
        };
      })
    );
  }, [handleQuantityChange, order.products, selectedRows, quantities]);

  const onReplaceAnOrder = () => {
    setIsLoadingReplaceOrder(true);
    const orderStatusWasFulfilled = order.status === 'FULFILLED';
    axios
      .post(`orders`, {
        account: customer.id,
        order_products: selectedRows.map((index) => {
          const orderProduct = tableData[index];
          return {
            type: 'REPLACEMENT',
            product_code: orderProduct.code,
            pet_plan: orderProduct.pet_plan.id,
            quantity: quantities[index],
            replacement_parent_order_product: orderProduct.id,
            parent_order_product: orderProduct.id,
          };
        }),
        order_type: 'REPLACEMENT',
        replacement_parent_order: order.id,
        parent_order: order.id,
        shipping_method: selectedShippingOption.value,
      })
      .then((response) => {
        let replacementSuccess = `Replacement order ${response.data.id} successfully created. `;
        if (orderStatusWasFulfilled) {
          toast.success(
            (replacementSuccess +=
              'Reminder: Please mark the original fulfillment(s) as “DELIVERY FAILED”'),
            BOTTOM_TOAST
          );
        } else {
          toast.success(replacementSuccess, BOTTOM_TOAST);
        }
        setShowConfirmModal(false);
        timeoutRef.current = setTimeout(() => {
          const urlParts = window.location.href.split('/');
          // Remove the last 2 segments (2 levels up)
          urlParts.pop();
          urlParts.pop();
          location.href = urlParts.join('/');
        }, 5000);
      })
      .catch((e) => {
        toast.error(generateErrorMsg(e), BOTTOM_TOAST);
      })
      .finally(() => {
        setIsLoadingReplaceOrder(false);
        onConfirm();
      });
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 modal">
      <div className="bg-white rounded-lg shadow-md p-4 min-w-[700px] relative border overflow-y-auto max-h-[90vh]">
        <Close className="absolute top-1 right-1 w-8 h-8 cursor-pointer" onClick={onCancel} />
        <h1 className="font-bold text-left mb-2">Replace Order</h1>
        <LineInfo
          title="Customer"
          value={`${getCountryFlag(customer.default_country)} ${generateFullName(
            customer.first_name,
            customer.last_name
          )} (${customer.email})`}
        />
        <LineInfo title="Primary Shipping Address" value={shippingAddress} />
        <hr />
        <div className="bg-white pt-2">
          <b>Which product(s) should we include in this replacement order?</b>
          <CheckboxTable
            header={ReplacementOrderHeader}
            data={tableData}
            selectedRows={selectedRows}
            onCheckboxChange={handleCheckboxChange}
          />
        </div>
        <div className="w-full pr-2 flex flex-col gap-2">
          <h3 className="font-bold text-left mb-1 pt-4">
            Which shipping method should we apply to this replacement order?
          </h3>
          <div className="mb-4">
            {isLoadingShippingRates ? (
              <div className="flex items-center justify-center py-4">
                <div className="text-gray-500">Loading shipping options...</div>
              </div>
            ) : (
              <Dropdown
                options={shippingOptions}
                selectedOption={selectedShippingOption}
                setSelectedOption={setSelectedShippingOption}
              />
            )}
          </div>
        </div>
        <div className="flex justify-center py-4">
          <Button
            isDisabled={
              selectedRows.length === 0 || !selectedShippingOption.value || isLoadingShippingRates
            }
            label="Replace Order"
            variant={
              selectedRows.length === 0 || !selectedShippingOption.value || isLoadingShippingRates
                ? 'secondary'
                : 'primary'
            }
            onClick={() => setShowConfirmModal(true)}
          />
        </div>
      </div>
      {showConfirmModal && (
        <ConfirmationModal
          title="Are you sure you want to proceed with this replacement order?"
          message=""
          customMessage={
            <p className="text-sm mb-4 text-center">
              This action will create a new order that is processed immediately. The customer will{' '}
              <strong>not</strong> be charged for this replacement order.
            </p>
          }
          confirmLabel="Confirm"
          isProcessLoading={isLoadingReplaceOrder}
          onConfirm={onReplaceAnOrder}
          onCancel={() => setShowConfirmModal(false)}
        />
      )}
    </div>
  );
};
